import { useEffect, useMemo, useState, useRef } from 'react';
import { ROW_URL, BASE_URL } from '$core/helpers/BASE_URL';
import { useGetProducts } from '$core/api/useGetProducts';
import { useGlobalContext } from '../../../contexts';
import itStrings from '$core/intl/strings.it.json';
import enStrings from '$core/intl/strings.en.json';

type ProductListProps = {
  cat: string;
  selectedStrumento: string;
};

const unique = (values: (string | undefined)[]): string[] => (
  [...new Set(values.filter((v): v is string => v !== undefined && v !== null))]
);

const ProductList = ({ cat, selectedStrumento }: ProductListProps) => {
  const baseU = ROW_URL();
  const { language } = useGlobalContext();
  const strings = language === 'it' ? itStrings : enStrings;

  const { product: prod, loading } = useGetProducts({
    productType: cat,
    selectedStrumento,
  });

  const [selectedModello, setSelectedModello] = useState('');
  const [selectedLegno, setSelectedLegno] = useState('');
  const [selectedFiniture, setSelectedFiniture] = useState('');
  const [selectedMetallo, setSelectedMetallo] = useState('');
  const [modelloActiveButton, setModelloActiveButton] = useState('');
  const [legnoActiveButton, setLegnoActiveButton] = useState('');
  const [finitureActiveButton, setFinitureActiveButton] = useState('');
  const [metalloActiveButton, setMetalloActiveButton] = useState('');

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      setSelectedModello('');
      setModelloActiveButton('');
      setSelectedLegno('');
      setLegnoActiveButton('');
      setSelectedFiniture('');
      setFinitureActiveButton('');
      setSelectedMetallo('');
      setMetalloActiveButton('');
    }
  }, [selectedStrumento]);

  const modelloMenu = useMemo(() => unique(prod.map((p) => p.modello)), [prod]);
  const legniMenu = useMemo(() => unique(prod.map((p) => p.legno)), [prod]);
  const finitureMenu = useMemo(() => unique(prod.map((p) => p.finiture)), [prod]);
  const metalloMenu = useMemo(() => unique(prod.map((p) => p.metallo)), [prod]);

  const filteredProducts = useMemo(() => {
    let result = prod;
    if (selectedLegno) result = result.filter((p) => p.legno === selectedLegno);
    if (selectedModello) result = result.filter((p) => p.modello === selectedModello);
    if (selectedStrumento) result = result.filter((p) => p.strumento === selectedStrumento);
    if (selectedFiniture) result = result.filter((p) => p.finiture === selectedFiniture);
    if (selectedMetallo) result = result.filter((p) => p.metallo === selectedMetallo);
    return result;
  }, [prod, selectedLegno, selectedModello, selectedStrumento, selectedFiniture, selectedMetallo]);

  return (
    <div className="bg-naturisima-dark">
      <div>
        <div className="pt-1">
          <h5
            className="text-center text-yellow-500 italic text-sm uppercase"
          >
            {strings.modello}
          </h5>
          <div className="flex flex-nowrap justify-center">
            <div>
              <ul className="flex flex-wrap justify-center">
                {modelloMenu.map((modello) => (
                  <button
                    key={modello}
                    type="button"
                    className={`text-sm w-auto bg-button-dark m-1 py-1 px-3 border-2 uppercase text-white rounded-xl  ${modelloActiveButton === modello ? 'border-amber-500' : 'border-transparent'}`}
                    onClick={() => {
                      if (modelloActiveButton === modello) {
                        setSelectedModello('');
                        setModelloActiveButton('');
                      } else {
                        setSelectedModello(modello);
                        setModelloActiveButton(modello);
                      }
                    }}
                  >
                    <div>{modello}</div>
                  </button>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {legniMenu.length > 0 && (
        <div>
          <div className="pt-4">
            <h5
              className="text-center text-yellow-500 italic text-sm uppercase"
            >
              {strings.legno}
            </h5>
            <div className="flex flex-nowrap justify-center">
              <div>
                <ul className="flex flex-wrap justify-center">
                  {legniMenu.map((legno) => (
                    <button
                      key={legno}
                      type="button"
                      className={`text-sm w-auto m-1 py-1 px-3 uppercase text-white border-2 bg-button-dark rounded-xl ${legnoActiveButton === legno ? 'border-amber-500' : 'border-transparent'}`}
                      onClick={() => {
                        if (legnoActiveButton === legno) {
                          setSelectedLegno('');
                          setLegnoActiveButton('');
                        } else {
                          setSelectedLegno(legno);
                          setLegnoActiveButton(legno);
                        }
                      }}
                    >
                      <div>{legno}</div>
                    </button>
                  ))}
                </ul>
              </div>
              <div />
            </div>
          </div>
        </div>
      )}

      {metalloMenu.length > 0 && (
        <div className="flex flex-nowrap justify-center">
          <div className="pt-4">
            <h5
              className="text-center text-yellow-500 italic text-sm uppercase"
            >
              {strings.metallo}
            </h5>
            <div>
              <ul className="flex flex-wrap justify-center">
                {metalloMenu.map((metallo) => (
                  <button
                    key={metallo}
                    type="button"
                    className={`text-sm w-auto m-1 py-1 px-3 uppercase text-white border-2 bg-button-dark rounded-xl ${metalloActiveButton === metallo ? 'border-amber-500' : 'border-transparent'}`}
                    onClick={() => {
                      if (metalloActiveButton === metallo) {
                        setSelectedMetallo('');
                        setMetalloActiveButton('');
                      } else {
                        setSelectedMetallo(metallo);
                        setMetalloActiveButton(metallo);
                      }
                    }}
                  >
                    <div>{metallo}</div>
                  </button>
                ))}
              </ul>
            </div>
            <div />
          </div>
        </div>
      )}

      {finitureMenu.length > 0 && (
        <div>
          <div className="flex flex-nowrap justify-center">
            <div className="pt-4">
              <h5
                className="text-center text-yellow-500 italic text-sm uppercase"
              >
                {strings.finiture}
              </h5>
              <div>
                <ul className="flex flex-wrap justify-center">
                  {finitureMenu.map((finiture) => (
                    <button
                      key={finiture}
                      type="button"
                      className={`text-sm w-auto m-1 py-1 px-3 uppercase text-white border-2 bg-button-dark rounded-xl ${finitureActiveButton === finiture ? 'border-amber-500' : 'border-transparent'}`}
                      onClick={() => {
                        if (finitureActiveButton === finiture) {
                          setSelectedFiniture('');
                          setFinitureActiveButton('');
                        } else {
                          setSelectedFiniture(finiture);
                          setFinitureActiveButton(finiture);
                        }
                      }}
                    >
                      <div>{finiture}</div>
                    </button>
                  ))}
                </ul>
              </div>
              <div />
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="text-white text-sm">
            Caricamento prodotti...
          </div>
        </div>
      )}

      {!loading && (
        <ul className="flex justify-between flex-wrap mx-auto py-8">
          {filteredProducts.map(({
            internalId,
            name,
            imageUrl,
            alt,
            legno,
            finiture,
            strumento,
            modello,
            metallo,
          }) => (
            <div className="lg:w-4/12 mx-auto">
              <li
                key={internalId}
                className="lg:w-auto my-2 mx-auto flex-wrap content-center p-4 lg:m-1"
              >
                <a
                  href={`${BASE_URL()}product/${internalId}`}
                  className="text-decoration-none"
                >
                  <article
                    className="frontPage flex flex-col justify-center w-full"
                  >
                    <div className="flex flex-nowrap justify-between">
                      <h3
                        className="uppercase text-xs text-white my-2 border-b-2 border-yellow-500 pb-2 justify-start"
                      >
                        {strings[cat as keyof typeof strings]}
                      </h3>
                      <h3
                        className="uppercase justify-end text-xs text-white my-2 border-b-2 border-yellow-500 pb-2"
                      >
                        {strumento}
                      </h3>
                    </div>
                    <img
                      src={baseU + imageUrl}
                      alt={alt}
                      className="max-w-full h-64 rounded border bg-white p-1 dark:border-neutral-50 object-cover object-center"
                    />
                    <div
                      className="relative -top-12 right-4 text-right text-yellow-500 text-4xl"
                    >
                      ➜
                    </div>
                    <div
                      className="relative flex flex-nowrap justify-between -top-10"
                    >
                      {legno !== undefined && (
                        <div className="flex flex-col justify-start mt-2">
                          <h4
                            className="uppercase text-xs text-yellow-500 italic "
                          >
                            {strings.legno}
                          </h4>
                          <h3
                            className="uppercase text-xs text-white my-2 border-b-2 border-yellow-500 pb-2 justify-start"
                          >
                            {legno}
                          </h3>
                        </div>
                      )}
                      {metallo !== undefined && (
                        <div className="flex flex-col justify-start mt-2">
                          <h4
                            className="uppercase text-xs text-yellow-500 italic "
                          >
                            {strings.metallo}
                          </h4>
                          <h3
                            className="uppercase text-xs text-white my-2 border-b-2 border-yellow-500 pb-2 justify-start"
                          >
                            {metallo}
                          </h3>
                        </div>
                      )}
                      <div className="flex flex-col justify-center mt-2">
                        <h4
                          className="text-center uppercase text-xs text-yellow-500 italic "
                        >
                          {strings.modello}
                        </h4>
                        <h3
                          className="uppercase text-xs text-white my-2 border-b-2 border-yellow-500 pb-2 text-center"
                        >
                          {modello}
                        </h3>
                      </div>
                      {finiture !== undefined && (
                        <div className="flex flex-col justify-start mt-2">
                          <h4
                            className="text-right uppercase text-xs text-yellow-500 italic "
                          >
                            {strings.finiture}
                          </h4>
                          <h3
                            className="text-right uppercase text-xs text-white my-2 border-b-2 border-yellow-500 pb-2 justify-start"
                          >
                            {finiture}
                          </h3>
                        </div>
                      )}
                    </div>
                    <h2
                      className="relative -top-10 text-xs text-center mb-1 uppercase text-white hover:text-orange-500 text-base font-montserrat font-semibold h-4"
                    >
                      {name}
                    </h2>
                    <div
                      className="relative -top-10 flex justify-between my-2 border-b border-white pb-2"
                    />
                  </article>
                </a>
              </li>
            </div>
          ))}
        </ul>
      )}
    </div>
  );
};

export { ProductList };
